import ElementContainer from "../formElements/elementContainer";
import PageBreak from "../formElements/pageBreak";
import {FormElementType} from "~/enums/formElementType";
import IRepo from "../../../../common/interfaces/repo";
import Repo from "../../../../common/state/modules/repo";
import Selected from "../../store/modules/selected";
import WorkflowStep from "../workflow/workflowStep";
import Form from "../form";
import {IFormElementsInFormula} from "~/interfaces/propertyInterfaces";
import FormElement, { IFormElement } from "../formElements/formElement";
import FormElementCapabilityManager from "../formElements/formElementCapabilityManager";
import {ArrayHelper} from "~/helpers/arrayHelper";
import {IFormSection} from "~/interfaces/formSubmissionEditInterfaces"
import {FormSectionValidationMode} from "~/enums/formSectionValidationMode"
import { ModelType } from '~/enums/modelType';
import { GenericType } from '~/interfaces/genericTypeInterfaces';
import { IImposterOptions, IImposterFactory, IImposter } from '~/interfaces/imposterInterfaces';
import Imposter from '~/models/imposter';

export default class FormSection extends ElementContainer implements Omit<IFormSection, 'form_elements'>, IFormElementsInFormula, IImposterFactory {
  pages: string[] = [''];
  validation_mode: FormSectionValidationMode
  except = ['form', 'form_elements', 'createEmbeddedObject'];

  static repo: IRepo<FormSection> = new Repo<FormSection>('formSections', FormSection, { onFirstAdd: (item) => { Selected.formSection.set(item) } });

  get TRACKED_ARRAYS(): string[] {
    return [
      'pages'
    ];
  }

  get rootName(): string {
    return this.elementName;
  }

  get elementName(): string {
    return 'form_section';
  }

  get isCollectionRoot(): boolean {
    return false;
  }

  get modelType(): ModelType {
    return ModelType.FormSection;
  }

  get genericType(): GenericType {
    return ModelType.Object
  }

  get isDataCollection(): boolean {
    if (!this.workflowStep.isApprovalOrFormSectionStep) return false;

    return this.workflowStep.isThirdPartyMultiSection || this.workflowStep.isMultiSectionSet;
  }

  get isCollection(): boolean {
    return this.isDataCollection;
  }

  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    return new Imposter(this, options)
  }

  get workflowStep(): WorkflowStep {
    return this.parent;
  }

  get titleOrDefault(): string {
    return this.workflowStep.titleOrDefault;
  }

  get referenceNameOrDefault(): string {
    return this.titleOrDefault;
  }

  get form(): Form {
    return this.workflowStep.form
  }

  get pageCount(): number {
    return this.pages.length;
  }

  get pageBreaks(): PageBreak[] {
    return Object.keys(this.form_elements)
        .map(key => this.form_elements[key])
        .filter(<(a: IFormElement) => a is PageBreak>(fe => fe.element_type === FormElementType.pageBreak))
        .sort((a, b) => a.sort_order - b.sort_order);
  }

  buildPage(name: string = ''): void {
    this.pages.push(name);
  }

  movePageUp(index: number): void {
    if (index > 0) {
      const moveUpVal = this.pages[index];
      this.pages[index] = this.pages[index - 1];
      this.pages[index - 1] = moveUpVal;
    }
  }

  movePageDown(index: number): void {
    if (index < this.pages.length - 1) {
      const moveDownVal = this.pages[index];
      this.pages[index] = this.pages[index + 1];
      this.pages[index + 1] = moveDownVal;
    }
  }

  destroyPage(pageIndex: number = this.pages.length - 1): void {
    this.pages.splice(pageIndex, 1);
  }

  formElementsForFormula({includeRepeatables}:  {includeRepeatables?: boolean}, filter?: (el: IFormElement) => boolean): FormElement[] {
    const referenceableInFormulaTypes: FormElementType[] = ArrayHelper.diff(
      FormElementCapabilityManager.ALL_CONTROLS,
      // can't reference all of these types in formulas
      FormElementCapabilityManager.VISUAL_CONTROLS,
      FormElementCapabilityManager.TEMPLATE_CONTROLS,
      FormElementCapabilityManager.UPLOAD_CONTROLS,
      FormElementCapabilityManager.SIGNATURE_CONTROLS,
      FormElementCapabilityManager.LOOKUP_CONTROLS,
      FormElementCapabilityManager.LAYOUT_CONTAINER_CONTROLS,
    )
    const filters: Array<(el: IFormElement) => boolean> = [
      el => referenceableInFormulaTypes.includes(el.element_type)
    ]
    if (typeof filter === 'function') filters.push(filter)
    return this.subreferenceableFormElementsDeep(includeRepeatables, el => filters.every(f => f(el)))
  }
}